import {Injectable, inject} from '@angular/core'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {forkJoin, of} from 'rxjs'
import {map, mergeMap, switchMap} from 'rxjs/operators'
import {ToastNotificationService} from '@common/components/toast-notification/toast-notification.service'
import {Appointment} from '@models/appointment'
import {Tables} from '@models/db.models'
import {Outlet} from '@models/outlet'
import {Where} from '@models/where'
import {getOutletListRequest, setOutletsListByAppointment} from '@pages/home/store/home.actions'
import {ApiService} from '@service/api.service'
import {AppointmentService} from '@service/appointment.service'
import {AuthService} from '@service/auth/auth.service'
import {OutletService} from '@service/outlet/outlet.service'
import {TranslationServicex} from '@service/translationx.service'
import {ScheduleActionsTypes, getAppointmentsResponse, removeScheduleAppointmentResponse} from './schedule.actions'
import {LoggingService} from '@service/logging.service'
import {UveLogKey} from '@models/constants/log-key'

export type DateEndDate = {
  start: number
  end: number
}

@Injectable()
export class ScheduleEffects {
  private readonly actions$ = inject(Actions)
  private readonly outletService = inject(OutletService)
  private readonly appointmentsService = inject(AppointmentService)
  private readonly apiService = inject(ApiService)
  private readonly loggingService = inject(LoggingService)
  private readonly translationServicex = inject(TranslationServicex)
  private readonly authService = inject(AuthService)
  private readonly toastNotificationService = inject(ToastNotificationService)

  appointmentRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActionsTypes.GET_APPOINTMENTS_REQUEST),
      switchMap((props: {filters?: Array<Where>}) => {
        const filters = props?.filters || []

        return this.appointmentsService.getAllAppointmentsWithUser(filters)
      }),
      switchMap(appointments => {
        const outletId = []

        const appointmentsData = appointments.map(appointment => {
          return {
            ...appointment,
            appointmentDuration: this.appointmentsService.getDurationAppointment(appointment.appointmentDuration),
          }
        })

        appointmentsData.forEach(appointment => {
          if (!outletId.includes(appointment.targetId)) {
            outletId.push(appointment.targetId)
          }
        })

        return forkJoin([
          of(appointmentsData),
          this.outletService.getBulkOutlets(outletId, this.authService.uveUser.userId.toString(), [], []),
        ])
      }),
      map(([appointments, outlets]: [Appointment[], Outlet[]]) => {
        let stateOutlets: Record<string, Outlet> = {}
        outlets.forEach(outlet => {
          if (outlet.id) {
            stateOutlets = {
              ...stateOutlets,
              [outlet?.id?.toString()]: outlet,
            }
          }
        })
        const allAppointments = appointments
          ?.filter(a => stateOutlets[a?.targetId?.toString()])
          .map(a => ({
            ...a,
            outletInfo: stateOutlets[a?.targetId?.toString()],
          }))
        return getAppointmentsResponse({
          appointments: allAppointments,
        })
      })
    )
  )

  removeAppointmentRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActionsTypes.REMOVE_APPOINTMENT_REQUEST),
      switchMap(({appointment}: {appointment: Appointment}) =>
        forkJoin({
          result: this.apiService.delete<Appointment>(Tables.Appointments, appointment.appointmentId, {
            ...appointment,
            _id: appointment.appointmentId,
          }),
          appointment: of(appointment),
        })
      ),
      switchMap(async ({result, appointment}: {result: boolean; appointment: Appointment}) => {
        const outlet = this.outletService.mapOutlet(appointment.outletInfo)
        return {outlet, result, appointment}
      }),
      mergeMap(({outlet, result, appointment}) => {
        const actions = []

        if (result) {
          this.toastNotificationService.success(this.translationServicex.instant('removed_successfully'))
          actions.push(removeScheduleAppointmentResponse({appointment}))
        } else {
          this.toastNotificationService.error(this.translationServicex.instant('deleting_error'))
        }

        actions.push(getOutletListRequest())
        actions.push(
          setOutletsListByAppointment({
            outlet,
            appointment: null,
            appointmentOld: appointment,
          })
        )
        this.loggingService.debug(UveLogKey.Debug.DeleteAppointment, {
          value: {
            outletId: appointment.targetId,
            appointmentId: appointment.appointmentId,
          },
        })
        return actions
      })
    )
  )
}
